import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useStore } from '@shared/hooks/useStore';

const ReaderPagination = observer(() => {
    const { readerStore } = useStore();
    const activeBook = readerStore.getActiveBook();
    const [inputValue, setInputValue] = useState('1');

    useEffect(() => {
        if (activeBook) {
            setInputValue(String(activeBook.currentPage || 1));
        }
    }, [activeBook?.currentPage]);

    if (!activeBook) return null;

    const { currentPage } = activeBook;
    const isPreview = activeBook.publication.isPreview;
    const totalPages = isPreview ?
        Math.min(activeBook.publication.previewPageCount || 15, activeBook.publication.totalNumberPages) :
        activeBook.publication.totalNumberPages;

    const progress = totalPages > 0 ? (currentPage / activeBook.publication.totalNumberPages) * 100 : 0;

    const goToPage = (page: number) => {
        const validPage = Math.max(1, Math.min(page, totalPages));
        readerStore.fetchPage(activeBook.publication.id, validPage);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            setInputValue(value);
        }
    };

    const handleInputBlur = () => {
        const page = parseInt(inputValue);
        if (isNaN(page)) {
            setInputValue(String(currentPage));
            return;
        }

        if (page === currentPage) {
            return;
        }

        if (page >= 1 && page <= totalPages) {
            goToPage(page);
        } else if (page < 1) {
            setInputValue(String(1));
            goToPage(1);
        } else if (page > totalPages) {
            setInputValue(String(totalPages));
            goToPage(totalPages);
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleInputBlur();
        }
    };

    const getButtonClass = (disabled: boolean) => {
        const base = 'p-2 transition-colors';
        return disabled ?
            `${base} text-gray-600 cursor-not-allowed` :
            `${base} text-white hover:bg-[#FFFFFF1A]`;
    };

    const isFirst = currentPage === 1;
    const isLast = currentPage === totalPages;

    return (
        <div id='reader-pagination' className='flex-shrink-0 flex flex-col gap-2 pt-1 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] sm:py-1 border-t border-[#FFFFFF1A] px-2 sm:px-4 h-[42px] sm:h-[40px] relative'>
            <div className='flex items-center justify-center'>
                <button
                    onClick={() => goToPage(1)}
                    disabled={isFirst}
                    className={getButtonClass(isFirst)}
                    title='最初'
                >
                    <ChevronsLeft size={18} />
                </button>

                {/* Previous Page (Mobile) */}
                <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={isFirst}
                    className={`${getButtonClass(isFirst)} sm:hidden`}
                    title='前のページ'
                >
                    <ChevronLeft size={18} />
                </button>

                {/* Page Input */}
                <div className='flex items-center gap-2 px-2'>
                    <input
                        type='text'
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        onKeyDown={handleInputKeyDown}
                        maxLength={15}
                        className='w-[44px] h-[26px] text-center bg-[#FFFFFF1A] font-semibold text-white rounded-[3px] text-sm leading-[26px] focus:outline-none focus:border-primary'
                    />
                    <span className='text-white font-semibold text-sm leading-[26px]'>
                        /
                        {' '}
                        {activeBook.publication.totalNumberPages}
                    </span>
                </div>

                {/* Next Page (Mobile) */}
                <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={isLast}
                    className={`${getButtonClass(isLast)} sm:hidden`}
                    title='次のページ'
                >
                    <ChevronRight size={18} />
                </button>

                {/* Last Page */}
                <button
                    onClick={() => goToPage(totalPages)}
                    disabled={isLast}
                    className={getButtonClass(isLast)}
                    title='最後'
                >
                    <ChevronsRight size={18} />
                </button>
            </div>
            {/* <div className='w-full flex-shrink-0 h-[6px] rounded-full overflow-hidden bg-[#FFFFFF1A]'>
                <div
                    className='h-full bg-[#D9D9D9] transition-all'
                    style={{ width: `${progress}%` }}
                />
            </div> */}
        </div>
    );
});

export default ReaderPagination;
